import type { ScorableEvent } from '@loci/types';

/**
 * toScorableEvent — maps a persisted Prisma `Event` row (with its `location`,
 * `categories`, and `eventSource` relations included) to the flat, normalized
 * scorer-input view `ScorableEvent` (COR-17).
 *
 * ## Coordinate decoding
 *
 * The persisted `Event.coordinates` column is PostGIS `geography(POINT, 4326)`,
 * declared `Unsupported(...)` in the Prisma schema. A plain Prisma query does
 * NOT return it as usable lat/lng — call sites decode it via raw SQL with
 * `ST_Y(coordinates::geometry) AS latitude` / `ST_X(...) AS longitude` (the
 * pattern used throughout `packages/db-core/src/client.ts`).
 *
 * Rather than re-run PostGIS here, this mapper reads already-decoded
 * coordinates defensively, in priority order:
 *   1. Top-level decoded numbers (`event.latitude` / `event.longitude`) — what
 *      the raw-SQL `ST_Y`/`ST_X` projection attaches.
 *   2. A decoded `event.coordinates = { lat, lng }` object — the shape the
 *      `db-core` middleware / GeoJSON decoders produce.
 *   3. The `location` relation's decoded coordinates (same two shapes), since
 *      most events inherit their point from the venue.
 *
 * Anything else (raw WKB string/Buffer, `null`, `undefined`, NaN, out-of-range)
 * decodes to `null` — the scorer treats missing coordinates as a quality gap
 * rather than trusting a bogus value.
 */

/** A decoded `{ lat, lng }` point as produced by db-core's GeoJSON decoders. */
interface DecodedPoint {
  readonly lat?: number | null;
  readonly lng?: number | null;
  readonly latitude?: number | null;
  readonly longitude?: number | null;
}

/** Defensive view of the `location` relation included on the event. */
interface ScorableLocationInput {
  readonly name?: string | null;
  readonly address?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly country?: string | null;
  readonly postalCode?: string | null;
  readonly latitude?: number | null;
  readonly longitude?: number | null;
  readonly coordinates?: unknown;
}

/** Defensive view of one `EventCategory` join row with its `category` relation. */
interface ScorableEventCategoryInput {
  readonly category?: {
    readonly slug?: string | null;
    readonly name?: string | null;
  } | null;
}

/** Defensive view of the `eventSource` relation. */
interface ScorableEventSourceInput {
  readonly name?: string | null;
}

/**
 * Defensive view of one `EventArtist` join row with its `artist` relation
 * (PIP-146 — only the bio columns the `hasArtistBio` signal reads).
 */
interface ScorableEventArtistInput {
  readonly artist?: {
    readonly description?: string | null;
    readonly descriptionAttribution?: unknown;
  } | null;
}

/**
 * The input shape `toScorableEvent` accepts. A superset of the persisted Prisma
 * `Event` row with relations included. Coordinate fields are optional because
 * the PostGIS column only materializes as lat/lng when a caller decodes it.
 */
export interface ScorableEventInput {
  readonly id: string;
  readonly title: string;
  readonly description?: string | null;
  /** PIP-232 — `Event.importantInfo` (TM `info`). */
  readonly importantInfo?: string | null;
  /** PIP-232 — `Event.additionalInfo` (TM `pleaseNote`). */
  readonly additionalInfo?: string | null;
  readonly imageUrl?: string | null;
  readonly imageUrls?: unknown;
  readonly date: Date | string;
  readonly endDate?: Date | string | null;
  readonly timezone?: string | null;
  readonly lastSyncAt?: Date | string | null;
  readonly source?: string | null;
  readonly sourceUrl?: string | null;
  /**
   * The purchasable ticket link (`Event.ticketUrl`). Distinct from `sourceUrl`
   * (the provenance/origin URL): Ticketmaster rows routinely populate
   * `ticketUrl` while leaving `sourceUrl` NULL. The scorer reads only
   * `sourceUrl` (for both COMMERCE_TICKET_URL_INVALID and SOURCE_URL_MISSING),
   * so this mapper folds `ticketUrl` into `sourceUrl` as a fallback — mirroring
   * the ingest mapper (`eventDataToScorableEvent`, PIP-134). Without it a row
   * with a perfectly valid ticket URL was wrongly flagged "Ticket URL is
   * invalid" / "Source URL is missing".
   */
  readonly ticketUrl?: string | null;
  readonly sourceEventId?: string | null;
  readonly startTimeKnown?: boolean | null;
  readonly priceKnown?: boolean | null;
  /**
   * COR-115 — `Event.linkStatus`, the persisted link-liveness verdict. Passed
   * through unchanged; `dead` is the scorer's `SOURCE_LINK_DEAD` hard fail.
   * Every projection that re-scores a persisted row must select it, or the
   * re-score silently lifts a dead-link quarantine.
   */
  readonly linkStatus?: string | null;
  /**
   * PIP-407 — `Event.seriesOccurrences`, the series sweep's verdict (non-null
   * ⇔ evergreen-series member). Passed through; the scorer's `SERIES_EVERGREEN`
   * cap reads it. A re-score projection that omits it drops the cap until the
   * next sweep re-applies it.
   */
  readonly seriesOccurrences?: number | null;

  /**
   * PIP-161 price-story columns. Typed `unknown` for the Decimal pair because
   * Prisma returns `Decimal` objects (not numbers) for `Decimal?` columns, and
   * the raw-SQL projections return strings. {@link toFiniteNumberOrNull}
   * normalizes all three shapes without dragging a Prisma type dependency into
   * `@loci/validation`.
   */
  readonly priceMin?: unknown;
  readonly priceMax?: unknown;
  readonly isFree?: boolean | null;
  readonly rsvpOnly?: boolean | null;

  // Decoded coordinates (attached by a raw-SQL ST_Y/ST_X projection) or a
  // decoded coordinates object — both handled defensively.
  readonly latitude?: number | null;
  readonly longitude?: number | null;
  readonly coordinates?: unknown;

  // Relations
  readonly location?: ScorableLocationInput | null;
  readonly categories?: readonly ScorableEventCategoryInput[] | null;
  readonly eventSource?: ScorableEventSourceInput | null;
  /**
   * PIP-146 — the `EventArtist[]` join with each row's `artist` bio columns.
   * Optional: callers that don't include the join produce
   * `hasArtistBio: undefined` (signal N/A — neutral in the scorer).
   */
  readonly artists?: readonly ScorableEventArtistInput[] | null;
}

const LAT_MIN = -90;
const LAT_MAX = 90;
const LNG_MIN = -180;
const LNG_MAX = 180;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Returns the first argument that is a non-empty string after trimming, else
 * `null`. Emptiness-aware (NOT `??`): a `''` or whitespace-only earlier value
 * must NOT shadow a real later one — the precise failure mode that lets a
 * blank `sourceUrl` swallow a valid `ticketUrl`.
 */
function firstNonEmptyString(...values: (string | null | undefined)[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/** Returns a finite latitude in [-90, 90], else `null`. */
function normalizeLat(value: unknown): number | null {
  return isFiniteNumber(value) && value >= LAT_MIN && value <= LAT_MAX ? value : null;
}

/** Returns a finite longitude in [-180, 180], else `null`. */
function normalizeLng(value: unknown): number | null {
  return isFiniteNumber(value) && value >= LNG_MIN && value <= LNG_MAX ? value : null;
}

/**
 * Pulls a `{ lat, lng }` pair out of a decoded coordinates object, accepting
 * either `{ lat, lng }` or `{ latitude, longitude }`. Returns `null` for either
 * axis that is missing or out of range.
 */
function readDecodedPoint(coordinates: unknown): { lat: number | null; lng: number | null } {
  if (coordinates == null || typeof coordinates !== 'object') {
    return { lat: null, lng: null };
  }
  const point = coordinates as DecodedPoint;
  const lat = normalizeLat(point.lat ?? point.latitude);
  const lng = normalizeLng(point.lng ?? point.longitude);
  return { lat, lng };
}

/**
 * Resolves decoded lat/lng from the event, then the location relation, in
 * priority order. Latitude and longitude are resolved together so a partial
 * source (only one axis present) does not get mixed with another source.
 */
function resolveCoordinates(event: ScorableEventInput): {
  latitude: number | null;
  longitude: number | null;
} {
  // 1. Top-level decoded numbers (raw-SQL ST_Y / ST_X projection).
  const topLat = normalizeLat(event.latitude);
  const topLng = normalizeLng(event.longitude);
  if (topLat !== null && topLng !== null) {
    return { latitude: topLat, longitude: topLng };
  }

  // 2. Top-level decoded coordinates object.
  const eventPoint = readDecodedPoint(event.coordinates);
  if (eventPoint.lat !== null && eventPoint.lng !== null) {
    return { latitude: eventPoint.lat, longitude: eventPoint.lng };
  }

  // 3. Location relation — decoded numbers, then decoded coordinates object.
  const location = event.location;
  if (location) {
    const locLat = normalizeLat(location.latitude);
    const locLng = normalizeLng(location.longitude);
    if (locLat !== null && locLng !== null) {
      return { latitude: locLat, longitude: locLng };
    }
    const locPoint = readDecodedPoint(location.coordinates);
    if (locPoint.lat !== null && locPoint.lng !== null) {
      return { latitude: locPoint.lat, longitude: locPoint.lng };
    }
  }

  return { latitude: null, longitude: null };
}

/**
 * Flattens `Event.imageUrls` (a size-keyed JSON map like
 * `{ thumbnail, medium, large }`, or an array) plus the single `Event.imageUrl`
 * into a de-duplicated list of non-empty string URLs.
 */
function flattenImageUrls(imageUrls: unknown, imageUrl: string | null | undefined): string[] {
  const collected: string[] = [];

  const pushIfUrl = (value: unknown): void => {
    if (typeof value === 'string' && value.trim().length > 0) {
      collected.push(value);
    }
  };

  if (Array.isArray(imageUrls)) {
    for (const value of imageUrls) {
      pushIfUrl(value);
    }
  } else if (imageUrls != null && typeof imageUrls === 'object') {
    for (const value of Object.values(imageUrls as Record<string, unknown>)) {
      pushIfUrl(value);
    }
  }

  pushIfUrl(imageUrl);

  // De-duplicate while preserving first-seen order.
  return Array.from(new Set(collected));
}

/**
 * Flattens the `EventCategory[]` join into a list of category slugs (falling
 * back to category name). Skips join rows with no resolvable label and
 * de-duplicates.
 */
function flattenCategories(
  categories: readonly ScorableEventCategoryInput[] | null | undefined,
): string[] {
  if (!Array.isArray(categories)) {
    return [];
  }
  const labels: string[] = [];
  const firstNonEmpty = (...values: (string | null | undefined)[]): string | null => {
    for (const value of values) {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }
    return null;
  };
  for (const join of categories) {
    const category = join?.category;
    // Fall back on EMPTINESS, not just nullishness: an empty/whitespace slug
    // ('') must not short-circuit the name fallback via `??`.
    const label = firstNonEmpty(category?.slug, category?.name);
    if (label !== null) {
      labels.push(label);
    }
  }
  return Array.from(new Set(labels));
}

/**
 * PIP-146 — derive the tri-state `hasArtistBio` signal from the `EventArtist`
 * join: `undefined` when the join is absent/empty (signal N/A), otherwise
 * `true` iff any linked artist carries BOTH a non-empty `description` AND a
 * non-null `descriptionAttribution` (a real, attributed bio).
 */
function deriveHasArtistBio(
  artists: readonly ScorableEventArtistInput[] | null | undefined,
): boolean | undefined {
  if (!Array.isArray(artists) || artists.length === 0) {
    return undefined;
  }
  return artists.some((join) => {
    const artist = join?.artist;
    return (
      typeof artist?.description === 'string' &&
      artist.description.trim().length > 0 &&
      artist.descriptionAttribution != null
    );
  });
}

/**
 * PIP-161/167 — normalizes a persisted `Decimal?` price column to a finite
 * number, or `null`.
 *
 * Accepts the three shapes a `Decimal?` column actually arrives in:
 *   - a `number` (already coerced by a caller),
 *   - a `string` (raw-SQL projections return numerics as strings),
 *   - a Prisma `Decimal` object (duck-typed via `toString()`, so `@loci/validation`
 *     needs no Prisma dependency).
 *
 * Anything unparseable — `null`, `undefined`, `''`, `NaN`, `Infinity`, a bare
 * `{}` — returns `null`. That is the conservative direction: an unreadable price
 * is "no price evidence", never a fabricated `0`.
 */
function toFiniteNumberOrNull(value: unknown): number | null {
  if (value == null) return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  // Strings and Decimal-likes both resolve through their string form. Guard the
  // empty/whitespace case explicitly: `Number('')` is 0, which would fabricate
  // a "$0" price story out of a blank column.
  const asString =
    typeof value === 'string'
      ? value
      : typeof (value as { toString?: unknown }).toString === 'function'
        ? String(value)
        : '';
  if (asString.trim().length === 0) return null;

  const parsed = Number(asString);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Coerces a `Date | string | null | undefined` to `Date | null`. */
function toDateOrNull(value: Date | string | null | undefined): Date | null {
  if (value == null) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Maps a persisted Prisma `Event` row (with `location`, `categories`, and
 * `eventSource` relations included) into the flat `ScorableEvent` scorer input.
 *
 * Coordinates are read defensively from already-decoded fields — see the
 * module doc comment for the decode contract. Source prefers the scalar
 * `Event.source`, falling back to the `eventSource.name` relation.
 */
export function toScorableEvent(event: ScorableEventInput): ScorableEvent {
  const { latitude, longitude } = resolveCoordinates(event);
  const imageUrls = flattenImageUrls(event.imageUrls, event.imageUrl);
  const location = event.location ?? null;

  const source =
    (typeof event.source === 'string' && event.source.trim().length > 0
      ? event.source
      : undefined) ??
    (typeof event.eventSource?.name === 'string' && event.eventSource.name.trim().length > 0
      ? event.eventSource.name
      : null);

  // A null date is the downstream scorer's TEMPORAL_START_MISSING hard-fail
  // signal — never mask unparseable input with a NaN-timestamped Date.
  const date = toDateOrNull(event.date);

  return {
    id: event.id,
    title: event.title,
    description: event.description ?? null,
    importantInfo: event.importantInfo ?? null,
    additionalInfo: event.additionalInfo ?? null,

    imageUrls,
    hasImage: imageUrls.length > 0,

    latitude,
    longitude,

    venueName: location?.name ?? null,
    address: location?.address ?? null,
    city: location?.city ?? null,
    state: location?.state ?? null,
    country: location?.country ?? null,
    postalCode: location?.postalCode ?? null,

    categories: flattenCategories(event.categories),

    source,
    // Fold `ticketUrl` into `sourceUrl` (emptiness-aware) so a row whose ticket
    // link lives only in `Event.ticketUrl` is not falsely penalized for a
    // missing source/ticket URL — see the `ticketUrl` field doc above.
    sourceUrl: firstNonEmptyString(event.sourceUrl, event.ticketUrl),
    sourceEventId: event.sourceEventId ?? null,
    linkStatus: event.linkStatus ?? null,
    seriesOccurrences: event.seriesOccurrences ?? null,

    // Kept SEPARATE from the folded `sourceUrl` above: the price-story rule
    // (PIP-167) asks "can the user actually buy a ticket?", which a provenance
    // URL does not answer. Emptiness-aware so a whitespace-only column reads as
    // "no ticket link" rather than a truthy string.
    ticketUrl: firstNonEmptyString(event.ticketUrl),
    priceMin: toFiniteNumberOrNull(event.priceMin),
    priceMax: toFiniteNumberOrNull(event.priceMax),
    isFree: event.isFree ?? null,
    rsvpOnly: event.rsvpOnly ?? false,

    startTimeKnown: event.startTimeKnown ?? true,
    priceKnown: event.priceKnown ?? false,
    hasArtistBio: deriveHasArtistBio(event.artists),

    date,
    endDate: toDateOrNull(event.endDate),
    timezone: event.timezone ?? null,
    lastSyncAt: toDateOrNull(event.lastSyncAt),
  };
}
